/* eslint-disable jsx-a11y/label-has-associated-control */
import { Controller, useFormContext } from 'react-hook-form';
import { Subheader } from '../../tailwindComponents/Subheader';
import { Timepicker } from '../../tailwindComponents/Timepicker';
import { Input } from '../../tailwindComponents/Input';
import { Textarea } from '../../tailwindComponents/Textarea';
import { ToggleButtonGroup } from '../../tailwindComponents/ToggleButtonGroup';
import { TransportOrderFormValues } from './types';

interface ScheduleAndHandlingPanelProps {
  dispatchCutoffTime?: string | null;
  customerOperatingHoursFrom?: string | null;
  customerOperatingHoursTo?: string | null;
}

// Compact panel for time-window pickers + load-handling toggles in despatch-note
// mode. Dates and pallet specs live in the summary panel; only the editable
// bits surface here.
export function ScheduleAndHandlingPanel({
  dispatchCutoffTime,
  customerOperatingHoursFrom,
  customerOperatingHoursTo,
}: ScheduleAndHandlingPanelProps) {
  const { control, formState, watch, setValue } =
    useFormContext<TransportOrderFormValues>();

  const operatingHoursLabel =
    customerOperatingHoursFrom && customerOperatingHoursTo
      ? `${customerOperatingHoursFrom}–${customerOperatingHoursTo}`
      : null;

  const stackable = watch('stackable');
  const overhang = watch('overhang');

  return (
    <>
      <Subheader subHeader='Schedule and Handling' />
      <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-900 font-inter mb-2'>
            Collection time window
          </label>
          <div className='flex items-center gap-2'>
            <Controller
              name='collectionTimeFrom'
              control={control}
              render={({ field }) => (
                <Timepicker
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  isInvalid={!!formState.errors.collectionTimeFrom}
                  validationMessage={
                    formState.errors.collectionTimeFrom?.message
                  }
                />
              )}
            />
            <span className='text-sm text-gray-500'>to</span>
            <Controller
              name='collectionTimeTo'
              control={control}
              render={({ field }) => (
                <Timepicker
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  isInvalid={!!formState.errors.collectionTimeTo}
                  validationMessage={formState.errors.collectionTimeTo?.message}
                />
              )}
            />
          </div>
          <p className='text-xs text-gray-500 mt-1'>
            All times are in London time (GMT/BST)
            {dispatchCutoffTime && (
              <>
                {' · '}
                <span className='text-amber-700 font-medium'>
                  Warehouse cutoff: {dispatchCutoffTime}
                </span>
              </>
            )}
          </p>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-900 font-inter mb-2'>
            Delivery time window
            {operatingHoursLabel && (
              <span className='ml-2 text-xs font-normal text-amber-700'>
                (customer open {operatingHoursLabel})
              </span>
            )}
          </label>
          <div className='flex items-center gap-2'>
            <Controller
              name='deliveryTimeFrom'
              control={control}
              render={({ field }) => (
                <Timepicker
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  isInvalid={!!formState.errors.deliveryTimeFrom}
                  validationMessage={formState.errors.deliveryTimeFrom?.message}
                />
              )}
            />
            <span className='text-sm text-gray-500'>to</span>
            <Controller
              name='deliveryTimeTo'
              control={control}
              render={({ field }) => (
                <Timepicker
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  isInvalid={!!formState.errors.deliveryTimeTo}
                  validationMessage={formState.errors.deliveryTimeTo?.message}
                />
              )}
            />
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4 pt-3 border-t border-gray-100'>
          <div>
            <label className='block text-sm font-medium text-gray-900 font-inter mb-2'>
              Stackable
            </label>
            <ToggleButtonGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              selected={stackable ? 'yes' : 'no'}
              onChange={(value) => setValue('stackable', value === 'yes')}
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-900 font-inter mb-2'>
              Overhang beyond pallet footprint
            </label>
            <ToggleButtonGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              selected={overhang ? 'yes' : 'no'}
              onChange={(value) => setValue('overhang', value === 'yes')}
              variant={overhang ? 'warning' : 'primary'}
            />
          </div>
        </div>
        {overhang && (
          <Controller
            name='overhangDescription'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Overhang description'
                placeholder='Describe here...'
                sizeVariant='sm'
                name='overhangDescription'
                isInvalid={!!formState.errors.overhangDescription}
                validationMessage={
                  formState.errors.overhangDescription?.message
                }
              />
            )}
          />
        )}

        <Controller
          name='briefContents'
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              label='Brief contents description'
              placeholder='e.g. 12 boxes of electronics, palletised'
              isInvalid={!!formState.errors.briefContents}
              validationMessage={formState.errors.briefContents?.message}
            />
          )}
        />
      </div>
    </>
  );
}
